import {
  adjustToTimeZoneSafely,
  ContainerStatusEntity,
  containerStatusesSelector,
  getObjectId,
  useGetAllContainerStatusesQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useMemo} from "react";
import {ContainerStatusFilterFormValues} from "../components/form/ContainerStatusFilterForm/ContainerStatusFilterForm";
import {useApp} from "../providers/RegionProvider/RegionProvider";

export type UseGetContainerStatusesProps = {
  filter?: ContainerStatusFilterFormValues
  containerIds?: string[]
  serverScope?: boolean
}

export type UseGetContainerStatusesResult = {
  containerStatuses: ContainerStatusEntity[],
  isLoading: boolean
  isFetching: boolean
  lastFetchedDttm?: number
  refetch: () => void
}

export const useGetContainerStatuses = (props: UseGetContainerStatusesProps = {}): UseGetContainerStatusesResult => {
  const {filter, containerIds, serverScope = false} = props

  const {regionId, appCategory} = useApp()

  const selectContainerStatuses = useMemo(() => {
    return containerStatusesSelector();
  }, []);

  const queryArg = useMemo(() => {
    const companyId = getObjectId(filter?.company);
    const hasTagFilter = Boolean(filter?.tagDefinitions?.length);

    if (serverScope && filter?.assignedOnly === false && !companyId) {
      return skipToken
    }

    if (serverScope && hasTagFilter && (!containerIds || containerIds.length === 0)) {
      return skipToken
    }

    if (regionId && appCategory) {
      return {
        regionId: regionId,
        category: appCategory?.code,
        assignedOnly: serverScope ? (filter?.assignedOnly ?? true) : undefined,
        siteCompanyIds: serverScope && companyId ? [companyId] : undefined,
        containerIds: serverScope ? containerIds : undefined,
      }
    }

    return skipToken
  }, [regionId, appCategory, serverScope, filter?.assignedOnly, filter?.company, filter?.tagDefinitions?.length, containerIds])

  const {containerStatuses, isLoading, isFetching, fulfilledTimeStamp, refetch} = useGetAllContainerStatusesQuery(queryArg, {
    pollingInterval: 240000,
    refetchOnMountOrArgChange: 6 * 50,
    selectFromResult: (result) => ({
      ...result,
      containerStatuses: queryArg === skipToken ? [] : selectContainerStatuses(result, {
        containerIds: containerIds,
        companyId: getObjectId(filter?.company),
        operatingAreaId: getObjectId(filter?.operatingArea),
        routeGroupIds: filter?.routeGroups?.map(getObjectId),
        routeIds: filter?.routes?.map(getObjectId),
        siteId: getObjectId(filter?.site),
        assignedCompanyId: getObjectId(filter?.assignedCompany),
        unassignedOnly: filter?.unassignedOnly,
        transportViaPipeline: filter?.transportViaPipeline,
        transportOilViaPipeline: filter?.transportOilViaPipeline,
        usability: filter?.usability,
        restriction: filter?.restriction,
        showNoData: filter?.showNoData,
        showDoNotUse: filter?.showDoNotUse,
        highNonPrimaryVolumeOnly: filter?.highNonPrimaryVolumeOnly,
        criticalStatusOnly: filter?.criticalStatusOnly,
        availableLoadsOnly: filter?.availableLoadsOnly,
        lastDispatchedFromDttm: adjustToTimeZoneSafely(filter?.lastDispatchedFromDate)?.getTime(),
        lastDispatchedToDttm: adjustToTimeZoneSafely(filter?.lastDispatchedToDate)?.getTime(),
        lastHauledFromDttm: adjustToTimeZoneSafely(filter?.lastHauledFromDate)?.getTime(),
        lastHauledToDttm: adjustToTimeZoneSafely(filter?.lastHauledToDate)?.getTime(),
        neverDispatched: filter?.neverDispatched,
        neverHauled: filter?.neverHauled,
      }),
      isLoading: result.isLoading,
      isFetching: result.isFetching,
      fulfilledTimeStamp: result.fulfilledTimeStamp,
    })
  });

  const safeRefetch = useCallback(() => {
    if (queryArg !== skipToken) {
      refetch()
    }
  }, [queryArg, refetch])

  return {
    containerStatuses: containerStatuses,
    isLoading: isLoading,
    isFetching: isFetching,
    lastFetchedDttm: fulfilledTimeStamp,
    refetch: safeRefetch,
  }
}
